const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4000/api/v1";

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

// ── Token helpers – always read/write localStorage directly ──
export function setAccessToken(token: string | null) {
  if (token) {
    localStorage.setItem('accessToken', token);
  } else {
    localStorage.removeItem('accessToken');
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem('accessToken');
}

class ApiClientError extends Error {
  status: number;
  details?: unknown;
  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);

  // Always read the latest token from localStorage – never cache
  const token = getAccessToken();
  if (!options.skipAuth && token) {
    headers.set('Authorization', `Bearer ${token}`);
    headers.set('X-Auth-Token', token);          // InfinityFree fallback
  }

  if (!(options.body instanceof FormData) && options.body) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (response.status === 401 && !options.skipAuth && path !== '/auth/refresh') {
    const refreshed = await tryRefresh();
    if (refreshed) {
      const retryHeaders = new Headers(options.headers);
      const newToken = getAccessToken();
      if (!options.skipAuth && newToken) {
        retryHeaders.set('Authorization', `Bearer ${newToken}`);
        retryHeaders.set('X-Auth-Token', newToken);
      }
      if (!(options.body instanceof FormData) && options.body) {
        retryHeaders.set('Content-Type', 'application/json');
      }
      const retryResponse = await fetch(`${API_URL}${path}`, {
        ...options,
        headers: retryHeaders,
        credentials: 'include',
      });
      return parseResponse<T>(retryResponse);
    }
  }

  return parseResponse<T>(response);
}

async function parseResponse<T>(response: Response): Promise<T> {
  const isJson = response.headers.get('content-type')?.includes('application/json');
  const data = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      isJson && typeof data === 'object' && data && 'error' in data
        ? (data as any).error
        : 'Request failed.';
    throw new ApiClientError(response.status, message, isJson ? (data as any).details : undefined);
  }

  return data as T;
}

let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const storedRefresh = localStorage.getItem('refreshToken');
        if (!storedRefresh) return false;

        const headers = new Headers();
        headers.set('Authorization', `Bearer ${storedRefresh}`);
        headers.set('X-Auth-Token', storedRefresh);

        const res = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          headers,
          credentials: 'include',
        });

        if (!res.ok) return false;

        const data = await res.json();
        setAccessToken(data.accessToken);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        return true;
      } catch {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, {
      ...options,
      method: 'PATCH',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

export { ApiClientError };